import { CheckIcon } from '@heroicons/react/24/solid'
import { KeyboardEvent, ReactNode, useRef } from 'react'
import { cn } from 'src/utils/style'

export interface StepperStep {
  id: string
  label: ReactNode
  /** A step the user has already completed (rendered with a check). */
  complete?: boolean
  /** A locked/not-yet-reachable step: shown but not focusable or selectable. */
  disabled?: boolean
  'data-testid'?: string
}

interface StepperProps {
  steps: StepperStep[]
  currentId: string
  onStepSelect?: (id: string) => void
  /** Required: a tablist must be labelled. */
  'aria-label': string
  className?: string
  'data-testid'?: string
}

/**
 * Horizontal step indicator with `tablist`/`tab` semantics and roving-tabindex
 * keyboard navigation (Arrow/Home/End move focus across enabled steps;
 * Enter/Space activates — manual activation so focusing a step never navigates
 * on its own). Shared primitive for wizard step indicators — don't hand-roll a
 * row of `<button>`s.
 */
export function Stepper({
  steps,
  currentId,
  onStepSelect,
  'aria-label': ariaLabel,
  className,
  'data-testid': dataTestId,
}: StepperProps) {
  const refs = useRef<(HTMLButtonElement | null)[]>([])

  const enabledIndexes = steps.reduce<number[]>((acc, step, i) => {
    if (!step.disabled && onStepSelect) acc.push(i)
    return acc
  }, [])

  const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
    if ((e.key === 'Enter' || e.key === ' ') && !steps[index].disabled) {
      e.preventDefault()
      onStepSelect?.(steps[index].id)
      return
    }
    if (!enabledIndexes.length) return
    const pos = enabledIndexes.indexOf(index)
    let target: number | undefined
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        target = enabledIndexes[(Math.max(pos, 0) + 1) % enabledIndexes.length]
        break
      case 'ArrowLeft':
      case 'ArrowUp':
        target =
          enabledIndexes[(Math.max(pos, 0) - 1 + enabledIndexes.length) % enabledIndexes.length]
        break
      case 'Home':
        target = enabledIndexes[0]
        break
      case 'End':
        target = enabledIndexes[enabledIndexes.length - 1]
        break
      default:
        return
    }
    e.preventDefault()
    if (target !== undefined) refs.current[target]?.focus()
  }

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      aria-orientation="horizontal"
      data-testid={dataTestId}
      className={cn('flex flex-wrap items-center gap-1', className)}
    >
      {steps.map((step, i) => {
        const isCurrent = step.id === currentId
        const isNavigable = Boolean(onStepSelect) && !step.disabled
        return (
          <div key={step.id} className="flex items-center gap-1">
            <button
              ref={(el) => {
                refs.current[i] = el
              }}
              type="button"
              role="tab"
              aria-selected={isCurrent}
              aria-disabled={!isNavigable || undefined}
              disabled={!isNavigable}
              tabIndex={isCurrent ? 0 : -1}
              data-testid={step['data-testid']}
              data-current={isCurrent || undefined}
              data-complete={step.complete || undefined}
              onClick={() => isNavigable && onStepSelect?.(step.id)}
              onKeyDown={(e) => handleKeyDown(e, i)}
              className={cn(
                'flex items-center gap-2 rounded-full px-3 py-1 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-border-strong',
                isCurrent && 'bg-primary-100 text-primary-800',
                !isCurrent && step.complete && 'text-green-700',
                !isCurrent && !step.complete && 'text-text-tertiary',
                isNavigable && !isCurrent && 'hover:bg-surface-hover',
                !isNavigable && 'cursor-default',
              )}
            >
              <span
                className={cn(
                  'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-xs',
                  isCurrent && 'border-primary-500 bg-primary-500 text-white',
                  step.complete && 'border-green-500 bg-green-500 text-white',
                  !isCurrent && !step.complete && 'border-border-strong',
                )}
              >
                {step.complete ? <CheckIcon className="h-3 w-3" aria-hidden="true" /> : i + 1}
              </span>
              {step.label}
            </button>
            {i < steps.length - 1 && (
              <span className="text-text-tertiary" aria-hidden="true">
                &rsaquo;
              </span>
            )}
          </div>
        )
      })}
    </div>
  )
}
